import React, { useState, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import { BiEdit } from "react-icons/bi";
import { RiDeleteBin5Line } from "react-icons/ri";
import {
  MdMoreVert,
  MdLocalOffer,
  MdHistory,
  MdOutlinePayments,
  MdReceiptLong,
  MdMenuBook,
  MdPayments,
  MdAccountBalanceWallet,
  MdPrint,
} from "react-icons/md";
import { FiBookOpen } from "react-icons/fi";

/**
 * Row actions: ⋮ opens MUI Popover with Edit / Ledger / Payments / Add payment / etc.
 */
export default function RowActionsMenu({
  row,
  onEdit,
  onPrint,
  onEditPage,
  onDelete,
  onAddDiscount,
  onViewDiscountLog,
  onViewLedger,
  onOpeningBalance,
  onViewPayments,
  onAddPayment,
  onAddNonProductInvoice,
  iconColor = "#155f60",
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOpen = useCallback((e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }, []);

  return (
    <>
      <IconButton
        size="small"
        aria-label="row actions"
        onClick={handleOpen}
        sx={{ color: iconColor }}
      >
        <MdMoreVert />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 200,
              borderRadius: "12px",
              border: "1px solid #E5E9EB",
              boxShadow:
                "0 10px 30px rgba(16,69,70,0.12), 0 2px 8px rgba(16,69,70,0.08)",
            },
          },
        }}
      >
        <MenuList dense autoFocusItem>
          {typeof onPrint === "function" && (
            <MenuItem
              onClick={() => {
                onPrint(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdPrint className="text-lg text-inkSecondary" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Print
              </ListItemText>
            </MenuItem>
          )}
          {typeof onEdit === "function" && (
            <MenuItem
              onClick={() => {
                onEdit(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <BiEdit className="text-lg text-inkSecondary" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Edit
              </ListItemText>
            </MenuItem>
          )}
          {typeof onEditPage === "function" && (
            <MenuItem
              onClick={() => {
                onEditPage(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <FiBookOpen className="text-lg text-inkSecondary" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Edit Page #
              </ListItemText>
            </MenuItem>
          )}
          {typeof onAddDiscount === "function" && (
            <MenuItem
              onClick={() => {
                onAddDiscount(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdLocalOffer className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Add discount
              </ListItemText>
            </MenuItem>
          )}
          {typeof onViewDiscountLog === "function" && (
            <MenuItem
              onClick={() => {
                onViewDiscountLog(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdHistory className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Discount log
              </ListItemText>
            </MenuItem>
          )}
          {typeof onViewLedger === "function" && (
            <MenuItem
              onClick={() => {
                onViewLedger(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdMenuBook className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Ledger
              </ListItemText>
            </MenuItem>
          )}
          {typeof onOpeningBalance === "function" && (
            <MenuItem
              onClick={() => {
                onOpeningBalance(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdAccountBalanceWallet className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Opening Balance
              </ListItemText>
            </MenuItem>
          )}
          {typeof onAddPayment === "function" && (
            <MenuItem
              onClick={() => {
                onAddPayment(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdOutlinePayments className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Add Payment
              </ListItemText>
            </MenuItem>
          )}
          {typeof onViewPayments === "function" && (
            <MenuItem
              onClick={() => {
                onViewPayments(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdPayments className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Payments
              </ListItemText>
            </MenuItem>
          )}
          {typeof onAddNonProductInvoice === "function" && (
            <MenuItem
              onClick={() => {
                onAddNonProductInvoice(row);
                handleClose();
              }}
            >
              <ListItemIcon>
                <MdReceiptLong className="text-lg text-main" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                Non-product invoice
              </ListItemText>
            </MenuItem>
          )}
          {typeof onDelete === "function" && (
            <>
              {(typeof onPrint === "function" ||
                typeof onEdit === "function" ||
                typeof onEditPage === "function" ||
                typeof onAddDiscount === "function" ||
                typeof onViewDiscountLog === "function" ||
                typeof onViewLedger === "function" ||
                typeof onOpeningBalance === "function" ||
                typeof onViewPayments === "function" ||
                typeof onAddPayment === "function" ||
                typeof onAddNonProductInvoice === "function") && <Divider />}
              <MenuItem
                onClick={() => {
                  onDelete(row);
                  handleClose();
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <RiDeleteBin5Line className="text-lg text-danger" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={{ fontFamily: "Quicksand" }}>
                  Delete
                </ListItemText>
              </MenuItem>
            </>
          )}
        </MenuList>
      </Popover>
    </>
  );
}
